import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationSkipped, Router } from '@angular/router';
import { IonContent, ViewDidEnter, ViewDidLeave } from '@ionic/angular';
import { QuickOrderItem } from '@order/models/quick-order-entry-view-model';
import { OrderService } from '@order/services/order.service';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { HotKeys } from '@shared/constants/hot-key.enum';
import { FEATURES } from '@shared/constants/splitio-features';
import { OrderItemService } from '@shared/services/order-item.service';
import { QuickOrderEntryService } from '@shared/services/quick-order/quick-order-entry.service';
import {
  BehaviorSubject,
  distinctUntilChanged,
  Subscription,
  take,
} from 'rxjs';
import {
  QuickOrderEntryInputTypeEnum,
  QuickOrderEntryToggleButtonEnum,
} from './constants/quick-order-entry.constants';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { TranslateService } from '@ngx-translate/core';
import { OrderViewModel } from '@order/models/order-view-model';
import { OrderAnalyticsService } from '@order/tracking/analytics/order-analytics.service';
import { QuantityType } from '@shared/models/quantity-type';

@Component({
  selector: 'app-quick-order-entry',
  templateUrl: './quick-order-entry.page.html',
  styleUrls: ['./quick-order-entry.page.scss'],
})
export class QuickOrderEntryPage
  implements OnInit, OnDestroy, ViewDidLeave, ViewDidEnter
{
  @ViewChild(IonContent, { static: false }) content: IonContent;
  toggleBtnProductNum =
    QuickOrderEntryToggleButtonEnum.TOGGLE_BTN_PRODUCT_NUMBER;
  toggleBtnProductCustNum =
    QuickOrderEntryToggleButtonEnum.TOGGLE_BTN_CUST_NUMBER;
  selectProductTypeTab = this.toggleBtnProductNum;
  timerFocused: boolean;
  indexElementFocused: number;
  typeElementFocused: string;
  isOnMinimumOrderMetFlag: boolean;
  qoeCurrentOrderSub: Subscription;
  quickOrderItems: QuickOrderItem[] = [];
  breadcrumbs: Crumb[];
  routerNavigationSkipSub: Subscription;
  vm: OrderViewModel;
  quickOrderItems$: BehaviorSubject<QuickOrderItem[]> = new BehaviorSubject<
    QuickOrderItem[]
  >([]);

  constructor(
    readonly platformService: PlatformService,
    readonly quickOrderEntryService: QuickOrderEntryService,
    protected panAppState: PanAppState,
    private orderItemService: OrderItemService,
    private orderService: OrderService,
    private translateService: TranslateService,
    readonly router: Router,
    private orderAnalyticsService: OrderAnalyticsService,
  ) {}

  ionViewDidLeave(): void {
    if (!!this.routerNavigationSkipSub)
      this.routerNavigationSkipSub.unsubscribe();
    if (!!this.qoeCurrentOrderSub) this.qoeCurrentOrderSub.unsubscribe();
  }

  ngOnDestroy() {
    if (!!this.routerNavigationSkipSub)
      this.routerNavigationSkipSub.unsubscribe();
    if (!!this.qoeCurrentOrderSub) this.qoeCurrentOrderSub.unsubscribe();
  }

  ngOnInit() {
    this.indexElementFocused = 0;
    this.typeElementFocused =
      QuickOrderEntryInputTypeEnum.INPUT_TYPE_PRODUCT_NUMBER;
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.myOrders',
        url: '/desktop/my-orders-view',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.qoe',
        url: '',
        active: true,
      },
    ];
    this.toggleChange();
    this.panAppState
      .feature$([FEATURES.split_global_min_order_met.name])
      .pipe(take(1))
      .subscribe(flag => (this.isOnMinimumOrderMetFlag = flag));
  }

  ionViewWillEnter(): void {
    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.quickOrderEntry),
    );
  }

  ionViewDidEnter() {
    this.orderAnalyticsService.trackQoePageLoad();

    this.routerNavigationSkipSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationSkipped) {
        this.selectProductTypeTab = this.toggleBtnProductNum;
        this.toggleChange();
      }
    });

    this.qoeCurrentOrderSub = this.quickOrderEntryService
      ?.getCurrentOrder$()
      .subscribe(viewModel => (this.vm = viewModel));
  }

  async qoeProductUpdate(
    productIndex: number,
    newQOI: QuickOrderItem,
  ): Promise<void> {
    const qoi = this.quickOrderItems[productIndex];
    if ((isNaN(qoi?.productNumber) && !qoi?.customerProductNumber) || !newQOI)
      return;
    if (qoi.productNumber <= 0 || !qoi.product?.orderable) {
      this.quickOrderItems[productIndex].casesOrdered = newQOI.casesOrdered;
      this.quickOrderItems[productIndex].eachesOrdered = newQOI.eachesOrdered;
      return;
    }
    await this.orderService
      .getOrderItemWithProductNumber$(qoi.productNumber)
      .pipe(distinctUntilChanged(), take(1))
      .subscribe(orderItemInCart => {
        let newCaseQuantity, newEachQuantity;
        //CHECK AND UPDATE CASES
        if (
          newQOI.casesOrdered.currentValue >= 0 &&
          newQOI.casesOrdered.currentValue !== newQOI.casesOrdered.previousValue
        ) {
          newCaseQuantity = orderItemInCart?.unitsOrdered?.currentValue ?? 0;
          if (newQOI.casesOrdered.currentValue > 0) {
            newCaseQuantity += newQOI.casesOrdered.currentValue;
          } else {
            newCaseQuantity =
              newCaseQuantity - newQOI.casesOrdered.previousValue;
          }
          newQOI.casesOrdered.previousValue = newQOI.casesOrdered.currentValue;
          this.orderAnalyticsService.trackQOEAddToOrder(
            this.vm?.orderHeader,
            newQOI,
            QuantityType.Cases,
          );
        }
        //CHECK AND UPDATE EACHES
        if (
          qoi.product?.summary?.breakable &&
          newQOI.eachesOrdered.currentValue >= 0 &&
          newQOI.eachesOrdered.currentValue !==
            newQOI.eachesOrdered.previousValue
        ) {
          newEachQuantity = orderItemInCart?.eachesOrdered?.currentValue ?? 0;
          if (newQOI.eachesOrdered.currentValue > 0) {
            newEachQuantity += newQOI.eachesOrdered.currentValue;
          } else {
            newEachQuantity =
              newEachQuantity - newQOI.eachesOrdered.previousValue;
          }
          newQOI.eachesOrdered.previousValue =
            newQOI.eachesOrdered.currentValue;
          this.orderAnalyticsService.trackQOEAddToOrder(
            this.vm?.orderHeader,
            newQOI,
            QuantityType.Eaches,
          );
        }
        //UPDATE quickOrderItem Quantities
        if (
          (!isNaN(newCaseQuantity) &&
            newCaseQuantity >= 0 &&
            newCaseQuantity <= 999) ||
          (!isNaN(newEachQuantity) &&
            newEachQuantity >= 0 &&
            newEachQuantity <= 999)
        ) {
          this.orderItemService.changeMultiQuantity(
            qoi.productNumber,
            newCaseQuantity,
            newEachQuantity,
            null,
            true,
          );
        }
        this.quickOrderItems[productIndex].casesOrdered = newQOI.casesOrdered;
        this.quickOrderItems[productIndex].eachesOrdered = newQOI.eachesOrdered;
      });
  }

  async qoeProductSearch(
    productIndex: number,
    newQOI: QuickOrderItem,
    repeated?: boolean,
  ): Promise<void> {
    let qoeItem;
    let setFocus;
    try {
      if (
        !!newQOI &&
        this.selectProductTypeTab === this.toggleBtnProductCustNum &&
        !!newQOI.customerProductNumber
      ) {
        newQOI.productNumber =
          await this.quickOrderEntryService.qoeCustomerProductNumberSearch(
            newQOI?.customerProductNumber?.toString(),
          );
      }

      if (!newQOI) {
        qoeItem = {
          productNumber: null,
          customerProductNumber: null,
          loadingError: false,
          product: null,
          casesOrdered: {
            currentValue: undefined,
            previousValue: 0,
            loading: false,
            error: null,
          },
          eachesOrdered: {
            currentValue: undefined,
            previousValue: 0,
            loading: false,
            error: null,
          },
        };
      } else if (
        (this.selectProductTypeTab === this.toggleBtnProductNum &&
          (['', 'null', 'undefined'].includes(String(newQOI.productNumber)) ||
            newQOI.productNumber < 0)) ||
        (this.selectProductTypeTab === this.toggleBtnProductCustNum &&
          !newQOI.customerProductNumber)
      ) {
        qoeItem = newQOI;
        qoeItem.product = null;
      } else if (
        repeated ||
        (this.selectProductTypeTab === this.toggleBtnProductNum &&
          newQOI.productNumber !==
            this.quickOrderItems[productIndex].productNumber) ||
        (this.selectProductTypeTab === this.toggleBtnProductCustNum &&
          newQOI.customerProductNumber !==
            this.quickOrderItems[productIndex].customerProductNumber)
      ) {
        qoeItem = await this.quickOrderEntryService.qoeProductSearch(
          newQOI?.productNumber,
          1,
        );
        qoeItem.customerProductNumber = newQOI?.customerProductNumber;
        setFocus = !!qoeItem && this.indexElementFocused == productIndex;
        if (
          setFocus &&
          this.typeElementFocused ==
            QuickOrderEntryInputTypeEnum.INPUT_TYPE_PRODUCT_NUMBER
        ) {
          this.typeElementFocused =
            QuickOrderEntryInputTypeEnum.INPUT_TYPE_CASES;
        }
      }
    } catch (e) {}
    if (!qoeItem) return setFocus ? this.setFocus(100) : null;
    this.quickOrderItems$.next([]);
    this.quickOrderItems[productIndex] = qoeItem;
    if (!repeated) {
      await this.qoeProductUpdate(productIndex, newQOI);
    } else {
      this.quickOrderItems[productIndex].casesOrdered = newQOI.casesOrdered;
      this.quickOrderItems[productIndex].eachesOrdered = newQOI.eachesOrdered;
    }

    this.quickOrderItems$.next(this.quickOrderItems);
    return setFocus ? this.setFocus(100) : null;
  }

  addMoreRows(n: number): void {
    if (n <= 0) return;
    let counter = 0;
    while (counter < n) {
      this.quickOrderItems.push({
        productNumber: null,
        customerProductNumber: null,
        loadingError: counter++ < 0,
        product: null,
        casesOrdered: {
          currentValue: undefined,
          previousValue: 0,
          loading: false,
          error: null,
        },
        eachesOrdered: {
          currentValue: undefined,
          previousValue: 0,
          loading: false,
          error: null,
        },
      });
    }
    this.quickOrderItems$.next(this.quickOrderItems);
  }

  goToTop() {
    this.content.scrollToTop();
  }

  overrideFocus(productIndex: number, inputType: string) {
    if (
      this.indexElementFocused != productIndex ||
      this.typeElementFocused != inputType
    ) {
      this.indexElementFocused = productIndex;
      this.typeElementFocused = inputType;
      this.setFocus();
    }
  }

  changeFocus(productIndex: number, event: any) {
    const target = (event?.target as HTMLElement)?.ariaLabel;
    if (event.shiftKey && event.key == HotKeys.tab) {
      this.indexElementFocused = productIndex;
      this.typeElementFocused = target;
      switch (target) {
        case QuickOrderEntryInputTypeEnum.INPUT_TYPE_PRODUCT_NUMBER:
          if (productIndex > 0) {
            this.indexElementFocused = productIndex - 1;
            this.typeElementFocused =
              QuickOrderEntryInputTypeEnum.INPUT_TYPE_EACHES;
          }
          break;
        case QuickOrderEntryInputTypeEnum.INPUT_TYPE_CASES:
          this.typeElementFocused =
            QuickOrderEntryInputTypeEnum.INPUT_TYPE_PRODUCT_NUMBER;
          break;
        case QuickOrderEntryInputTypeEnum.INPUT_TYPE_EACHES:
          this.typeElementFocused =
            QuickOrderEntryInputTypeEnum.INPUT_TYPE_CASES;
          break;
      }
    } else if (
      event.key === HotKeys.enter ||
      event.key == HotKeys.tab ||
      event.type === HotKeys.paste
    ) {
      this.indexElementFocused = productIndex;
      this.typeElementFocused = target;
      switch (target) {
        case QuickOrderEntryInputTypeEnum.INPUT_TYPE_PRODUCT_NUMBER:
          this.typeElementFocused =
            QuickOrderEntryInputTypeEnum.INPUT_TYPE_CASES;
          break;
        case QuickOrderEntryInputTypeEnum.INPUT_TYPE_CASES:
          if (event.key === HotKeys.enter) {
            if (productIndex < this.quickOrderItems.length) {
              this.indexElementFocused = productIndex + 1;
              this.typeElementFocused =
                QuickOrderEntryInputTypeEnum.INPUT_TYPE_PRODUCT_NUMBER;
            }
          } else {
            this.typeElementFocused =
              QuickOrderEntryInputTypeEnum.INPUT_TYPE_EACHES;
          }
          break;
        case QuickOrderEntryInputTypeEnum.INPUT_TYPE_EACHES:
          if (productIndex < this.quickOrderItems.length) {
            this.indexElementFocused = productIndex + 1;
            this.typeElementFocused =
              QuickOrderEntryInputTypeEnum.INPUT_TYPE_PRODUCT_NUMBER;
          }
          break;
      }
    } else return;

    this.setFocus();
  }

  setFocus(timer: number = 0): void {
    if (this.timerFocused) return;
    this.timerFocused = true;
    setTimeout(() => {
      const target = this.quickOrderItems[this.indexElementFocused];
      if (!target) {
        this.timerFocused = false;
        return;
      }
      const parentElement = document
        .getElementById(`qoi-${this.indexElementFocused}`)
        ?.getElementsByClassName(this.typeElementFocused)[0] as HTMLElement;
      const actualElementFocused = parentElement?.getElementsByTagName(
        'input',
      )[0] as HTMLInputElement;
      if (!parentElement || !actualElementFocused) {
        this.typeElementFocused =
          QuickOrderEntryInputTypeEnum.INPUT_TYPE_PRODUCT_NUMBER;
        this.timerFocused = false;
        this.setFocus();
        return;
      }
      parentElement.classList.add('has-focus');
      actualElementFocused.select();
      actualElementFocused.focus();
      parentElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
      this.timerFocused = false;
    }, timer);
  }

  toggleChange(): void {
    this.quickOrderItems = [];
    this.quickOrderItems$.next(this.quickOrderItems);
    this.addMoreRows(
      this.platformService.getPlatformType() === PlatformEnum.mobile ? 10 : 100,
    );
  }
}
